// Salesforce Embedded Messaging. Until the bootstrap script defines its global —
// and on any page that does not embed chat — every helper here is a no-op.

type UtilAPI = {
  launchChat: (options?: { shouldStartNewConversation?: boolean }) => Promise<void>;
  hideChatButton: () => void;
  showChatButton: () => void;
};

type EmbeddedMessagingBootstrap = {
  settings: {
    language?: string;
    storageDomain?: string;
  };
  init: (orgId: string, deploymentName: string, siteUrl: string, options: { scrt2URL: string }) => void;
  utilAPI: UtilAPI;
  prechatAPI: {
    setHiddenPrechatFields: (fields: Record<string, string>) => void;
  };
};

declare global {
  interface Window {
    embeddedservice_bootstrap?: EmbeddedMessagingBootstrap;
  }
}

export type ChatDeployment = {
  orgId: string;
  deploymentName: string;
  siteUrl: string;
  scrt2Url: string;
};

const PRODUCTION_DEPLOYMENT: ChatDeployment = {
  orgId: "00D3t000003yZI4",
  deploymentName: "SimplyInsured_Support_Chat_Web",
  siteUrl: "https://simplyinsured.my.site.com/ESWSimplyInsuredSupport1787786963223",
  scrt2Url: "https://simplyinsured.my.salesforce-scrt.com",
};

// Support tier for routing. One value for every visitor: the same agents answer
// every queue, and Salesforce collects the topic itself.
export const SALESFORCE_CHAT_TIER = "L2";

// Keying session storage to the bare domain lets a conversation survive
// navigation between our subdomains.
export const SALESFORCE_CHAT_STORAGE_DOMAIN = "simplyinsured.com";

export type ChatVisitor = {
  readonly id?: string;
  readonly email?: string;
  readonly first_name?: string;
  readonly last_name?: string;
} | null;

// Hidden prechat fields, under the exact names the deployment expects. Empty string
// is Salesforce's "not known".
export function salesforceChatPrechatFields(visitor: ChatVisitor): Record<string, string> {
  return {
    tier: SALESFORCE_CHAT_TIER,
    customer_email: visitor?.email || "",
    customer_firstname: visitor?.first_name || "",
    customer_lastname: visitor?.last_name || "",
    account_id: visitor?.id || "",
    page_url: window.location.href,
  };
}

// Only the production deployment exists, so other environments get no chat.
export function salesforceChatDeployment(): ChatDeployment | null {
  return process.env.ENV_NAME === "production" ? PRODUCTION_DEPLOYMENT : null;
}

export function missingChatConfig(deployment: ChatDeployment): string[] {
  return Object.keys(deployment).filter((key) => !deployment[key]);
}

// Salesforce applies these only once it has drawn the launcher, so earlier calls
// wait for the button-created event, in order: a mount and unmount pair must not
// settle on the wrong state.
const queuedActions: Array<(util: UtilAPI) => void> = [];
let launcherReady = false;

window.addEventListener(
  "onEmbeddedMessagingButtonCreated",
  () => {
    launcherReady = true;
    const util = window.embeddedservice_bootstrap?.utilAPI;
    if (!util) return;
    while (queuedActions.length > 0) queuedActions.shift()(util);
  },
  { once: true },
);

function runOrDefer(action: (util: UtilAPI) => void): void {
  const bootstrap = window.embeddedservice_bootstrap;
  if (!bootstrap) {
    console.warn("Salesforce chat is not loaded on this page.");
    return;
  }

  if (launcherReady) {
    action(bootstrap.utilAPI);
    return;
  }

  queuedActions.push(action);
}

export function launchSalesforceChat(): void {
  runOrDefer((util) => {
    util.launchChat().catch((err) => console.error("Error launching Embedded Messaging: ", err));
  });
}

// Salesforce refuses to hide the launcher while a conversation window is open.
export function hideSalesforceChatButton(): void {
  runOrDefer((util) => util.hideChatButton());
}

export function showSalesforceChatButton(): void {
  runOrDefer((util) => util.showChatButton());
}
